import { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { ErrorAlertBanner, HStack, Text } from '@library';
import TableCell from '../../../../core/components/table/TableCell';
import ConditionalWidthTableCell from '../../../../core/components/table/ConditionalWidthTableCell';
import TableCellText from '../../../../core/components/table/TableCellText';
import TableRow from '../../../../core/components/table/TableRow';
import RestrictedContent from '../../../../core/components/router/RestrictedContent';
import useLoginState from '../../../../core/hooks/useLoginState';
import PendingClockIcon from '../../../../core/components/icons/PendingClockIcon';
import useProductQuantity from '../../../queries/useProductQuantity';
import ClickableArea from '../../../../core/components/button/ClickableArea';
import ProductStockIndicator from '../../../../availability/components/ProductStockIndicator';
import useCustomNavigate from '../../../../core/hooks/router/useCustomNavigate';
import useProductLink from '../../../hooks/useProductLink';
import type { ProductBackorder } from '../../../queries/useProductBackorders';
import ProductBackorderTooltip from '../../ProductBackorders/ProductBackorderTooltip';
import CustomLink from '../../../../core/components/CustomLink';
import ProductImage from '../../ProductImage/ProductImage';
import ProductQuantityControl from '../../../../core/components/ProductQuantityControl';
import type { ProductVariantsTableProductRowProps } from './types';
import styles from './ProductVariantsTable.module.scss';
import PriceColumns from './PriceColumns';

const ProductRowAttributeColumns = memo(
    ({
        fixedWidth,
        emphasizeSku,
        shouldShowSiblingsColumn,
        shouldShowVariantsColumn,
        siblingAttributes,
        variationAttributes,
        onVariantClick,
    }: Pick<
        ProductVariantsTableProductRowProps,
        | 'fixedWidth'
        | 'emphasizeSku'
        | 'shouldShowSiblingsColumn'
        | 'shouldShowVariantsColumn'
        | 'siblingAttributes'
        | 'variationAttributes'
    > & {
        onVariantClick(): void;
    }) => {
        return (
            <>
                {shouldShowSiblingsColumn &&
                    siblingAttributes?.map(({ value }) => (
                        <TableCell key={`table_cell_${value}`}>
                            <span>
                                <TableCellText>{value}</TableCellText>
                            </span>
                        </TableCell>
                    ))}
                {shouldShowVariantsColumn &&
                    variationAttributes?.map(({ name, value }) => (
                        <ConditionalWidthTableCell
                            fixedWidth={fixedWidth}
                            width="60px"
                            key={`table_cell_${value}`}
                        >
                            <ClickableArea
                                data-test-class={`productVariantsTable${name}Variant`}
                                role="link"
                                onClick={onVariantClick}
                                className={styles.productVariantPageLink}
                            >
                                <TableCellText emphasized={!emphasizeSku}>
                                    {value}
                                </TableCellText>
                            </ClickableArea>
                        </ConditionalWidthTableCell>
                    ))}
            </>
        );
    }
);

const PendingOrderedQuantityDictionaryCell = memo(
    ({ backorders }: { backorders?: ProductBackorder[] }) => {
        return (
            <TableCell>
                {backorders && (
                    <ProductBackorderTooltip backorders={backorders}>
                        <HStack gap="micro" align="center">
                            <PendingClockIcon />
                            <TableCellText>
                                {backorders.reduce(
                                    (sum, item) => item.pendingQuantity + sum,
                                    0
                                )}
                            </TableCellText>
                        </HStack>
                    </ProductBackorderTooltip>
                )}
            </TableCell>
        );
    }
);

const ProductStatusColumn = memo(
    ({
        sku,
        quantity,
        showAvailabilityMessage,
        backorders,
    }: {
        sku: string;
        quantity: number;
        showAvailabilityMessage?: boolean;
        backorders?: Record<string, ProductBackorder[]>;
    }) => {
        return (
            <>
                <TableCell>
                    <HStack justify="start">
                        <ProductStockIndicator
                            sku={sku}
                            desiredQuantity={quantity}
                            messageStyle={
                                showAvailabilityMessage ? 'right' : 'tooltip'
                            }
                        />
                    </HStack>
                </TableCell>
                {backorders && (
                    <PendingOrderedQuantityDictionaryCell
                        backorders={backorders[sku]}
                    />
                )}
            </>
        );
    }
);

const ProductVariantsTableProductRow = ({
    sku,
    showThumbnail = false,
    fixedWidth = false,
    customerPrice,
    product,
    variant,
    variantCount,
    shouldShowSiblingsColumn = false,
    shouldShowVariantsColumn = false,
    siblingAttributes,
    variationAttributes,
    actionsColumn,
    emphasizeSku = false,
    showAvailabilityMessage,
    showDiscountColumn,
    backorders,
}: ProductVariantsTableProductRowProps) => {
    const isLoggedIn = useLoginState();

    const navigate = useCustomNavigate();
    const productLink = useProductLink(variant?.id, product.id);
    const goToProductDetailsPage = useCallback(() => {
        navigate(productLink);
    }, [productLink, navigate]);

    const {
        quantity,
        updateQuantity,
        maxOrderQuantity,
        maxOrderQuantityLoading,
        loading,
    } = useProductQuantity({ sku });

    return (
        <>
            <TableRow data-test-class="productVariantsTableProduct" key={sku}>
                {showThumbnail && (
                    <TableCell
                        whiteBackground
                        rowSpan={variantCount}
                        valign="top"
                    >
                        <CustomLink
                            to={productLink}
                            data-test-class="productVariantsTableColorVariant"
                        >
                            <ProductImage
                                size="100px"
                                url={product.media[0]?.renditionUrl}
                                overviewPlacement="right"
                            />
                        </CustomLink>
                    </TableCell>
                )}
                <ConditionalWidthTableCell
                    fixedWidth={fixedWidth}
                    width="160px"
                >
                    <TableCellText emphasized={emphasizeSku}>
                        {sku}
                    </TableCellText>
                </ConditionalWidthTableCell>
                <ProductRowAttributeColumns
                    fixedWidth={fixedWidth}
                    emphasizeSku={emphasizeSku}
                    shouldShowSiblingsColumn={shouldShowSiblingsColumn}
                    shouldShowVariantsColumn={shouldShowVariantsColumn}
                    siblingAttributes={siblingAttributes}
                    variationAttributes={variationAttributes}
                    onVariantClick={goToProductDetailsPage}
                />
                {isLoggedIn && (
                    <ProductStatusColumn
                        sku={sku}
                        quantity={quantity}
                        showAvailabilityMessage={showAvailabilityMessage}
                        backorders={backorders}
                    />
                )}
                {customerPrice && customerPrice.retailPrice > 0 ? (
                    <>
                        <PriceColumns
                            customerPrice={customerPrice}
                            showDiscountColumn={showDiscountColumn}
                            fixedWidth={fixedWidth}
                            product={product}
                            variant={variant}
                        />
                        <ConditionalWidthTableCell
                            fixedWidth={fixedWidth}
                            width="124px"
                        >
                            <RestrictedContent
                                storeFlags={
                                    isLoggedIn
                                        ? undefined
                                        : 'cartSharingEnabled'
                                }
                            >
                                <ProductQuantityControl
                                    mini
                                    showErrorMessage
                                    max={maxOrderQuantity}
                                    id={`${sku}-quantity`}
                                    value={quantity}
                                    onChange={updateQuantity}
                                    loading={loading}
                                    maxQuantityLoading={maxOrderQuantityLoading}
                                />
                            </RestrictedContent>
                        </ConditionalWidthTableCell>
                    </>
                ) : (
                    <>
                        <TableCell></TableCell>
                        <TableCell colSpan={2}>
                            <ErrorAlertBanner noBorder>
                                <Text secondary>
                                    <FormattedMessage id="Price loading error" />
                                </Text>
                            </ErrorAlertBanner>
                        </TableCell>
                    </>
                )}

                {actionsColumn && (
                    <ConditionalWidthTableCell
                        fixedWidth={fixedWidth}
                        width="60px"
                    >
                        {actionsColumn(sku)}
                    </ConditionalWidthTableCell>
                )}
            </TableRow>
        </>
    );
};

export default memo(ProductVariantsTableProductRow);
